import L from 'leaflet';
import { LageplanGrab, Polygon } from '../../types';

// Kartendrehung, damit das Grab-Raster waagerecht ausgerichtet ist (im Lageplan und im
// Detail-Mini-Ausschnitt identisch). Positiv = im Uhrzeigersinn.
export const LAGEPLAN_BEARING = 222;

// Fill colors per Grab status for the site map.
export const STATUS_FILL: Record<string, string> = {
  FREI: '#22c55e',       // green
  BELEGT: '#6b7280',     // gray
  RESERVIERT: '#eab308', // yellow
  IN_NUTZUNG: '#3b82f6', // blue
};

export const STATUS_LABEL: Record<string, string> = {
  FREI: 'Frei', BELEGT: 'Belegt', RESERVIERT: 'Reserviert', IN_NUTZUNG: 'In Nutzung'
};

/**
 * Derives the main section (Hauptabteilung) from an abteilung label, e.g. "1A" -> "1".
 * Uses the leading numeric prefix; falls back to the trimmed full label if there is none.
 */
export const hauptAbteilung = (abteilung: string): string => {
  const m = abteilung.match(/^\s*(\d+)/);
  return m ? m[1] : abteilung.trim();
};

// Distinct colors for coloring graves by main section.
const ABTEILUNG_PALETTE = [
  '#2563eb', '#dc2626', '#16a34a', '#d97706', '#7c3aed',
  '#0891b2', '#db2777', '#65a30d', '#ea580c', '#0d9488',
];

/** Maps each main section present in the data to a stable color (sorted numerically). */
export const abteilungColorMap = (graeber: LageplanGrab[]): Record<string, string> => {
  const sections = Array.from(new Set(graeber.map(g => hauptAbteilung(g.abteilung))))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  const map: Record<string, string> = {};
  sections.forEach((s, i) => { map[s] = ABTEILUNG_PALETTE[i % ABTEILUNG_PALETTE.length]; });
  return map;
};

/**
 * GeoJSON stores coordinates as [lon, lat]; Leaflet expects [lat, lon].
 * Convert a polygon ring (outer ring of [lon,lat] points) to Leaflet positions.
 */
export const ringToLatLngs = (ring: Polygon): [number, number][] =>
  ring.map(([lon, lat]) => [lat, lon] as [number, number]);

/**
 * Average-of-vertices centroid of a ring, as Leaflet [lat, lon]. Good enough to place a small
 * marker inside a grave polygon (graves are tiny, roughly rectangular).
 */
export const ringCentroid = (ring: Polygon): [number, number] => {
  let lat = 0, lon = 0;
  ring.forEach(([lo, la]) => { lat += la; lon += lo; });
  const n = ring.length || 1;
  return [lat / n, lon / n];
};

/** Bounds spanning all grave polygons, for fitBounds. Null if there is no geometry. */
export const boundsOfGraeber = (graeber: LageplanGrab[]): L.LatLngBounds | null => {
  const points: [number, number][] = [];
  graeber.forEach(g => {
    if (g.polygon && g.polygon.length >= 3) {
      points.push(...ringToLatLngs(g.polygon));
    }
  });
  return points.length > 0 ? L.latLngBounds(points) : null;
};
